<template>
  <div
    class="border border-charcoal-500 text-light-950 dark:text-dark-100 dark:border-dark-600 dark:focus-within:border-dark-400 dark:border-opacity-100 border-opacity-30 rounded-md relative grow flex items-center"
    :class="[
      getStyle(styles, 'container'),
      size === 'base' ? (label ? 'pt-1.5 pb-1.5 px-2.5 text-md' : 'py-3.5 px-3.5 text-md') : '',
      size === 'sm' ? (label ? 'h-10 text-base px-1.5 py-3' : 'h-10 px-1.5 py-35') : '',
      isFocused ? 'border-charcoal-950 !border-opacity-100' : ''
    ]"
    @click="onClick"
  >
    <div class="grow">
      <label class="block font-medium" :class="[ size === 'base' ? 'text-md h-4' : 'text-sm h-3']" v-if="label">
        <span class="absolute origin-top-left line-clamp-1 break-all mr-1.5 transition pointer-events-none " :class="isActive ? 'scale-75 text-light-600 dark:text-dark-500 -translate-x-0 -translate-y-0' : 'translate-x-1.5 translate-y-2 text-light-600 dark:text-dark-500'">{{ label }}</span>
      </label>

      <component
        :id="id ? id : undefined"
        :is="type == 'textarea' ? 'textarea' : 'input'"
        :type="type"
        :placeholder="!label || isFocused || !label ? placeholder : ''"
        :required="isRequired"
        :name="name"
        class="!outline-none w-full border-none font-medium"
        :class="[
          getStyle(styles, 'input'),
          inputClasses
        ]"
        :value="modelValue"
        :disabled="isDisabled"
        :inputmode="inputMode"
        v-bind="{ autocomplete: isNoAutocomplete ? 'off' : (autocomplete ?? undefined), ...attrs }"
        @keyup="onChange"
        @change="onChange"
        @input="onChange"
        @focus="
          () => {
            isFocused = true
            $emit('focus', id)
          }
        "
        @blur="onBlur"
        ref="$input"
      />
    </div>
    <div class="space-x-2 flex items-center ml-2" v-if="Object.entries(helpers).length > 0">
      <component 
        v-for="helper in Object.entries(helpers)" 
        :is="getHelper(helper[0])"
        v-bind="helper[1]"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { defineStyles, getStyle } from '@/utils/useStyles'
import InputHelperIcon from './Helper/InputHelperIcon.vue'

const props = withDefaults(defineProps<{
  id?: string
  modelValue: string | number | undefined | null
  type?: string
  inputMode?: 'text' | 'numeric' | 'email' | 'search' | 'tel'
  inputClasses?: string
  isRequired?: boolean
  size?: 'base' | 'sm' | 'xs'
  isDisabled?: boolean
  isHideHelpers?: boolean
  align?: 'left' | 'center'
  isUppercase?: boolean
  color?: 'white' | 'transparent'
  autocomplete?: string
  name?: string
  label?: string
  isNoAutocomplete?: boolean
  placeholder?: string
  helpers?: { [key: string]: {} }
  attrs?: {}
}>(), {
  id: Math.random().toString(),
  type: 'text',
  inputMode: 'text',
  inputClasses: '',
  size: 'base',
  color: 'white',
  name: '',
  align: 'left',
  isRequired: false,
  isDisabled: false,
  isHideHelpers: false,
  isUppercase: false,
  isNoAutocomplete: false,
  label: '',
  placeholder: '',
  helpers: () => ({}),
  attrs: () => ({})
})

const emit = defineEmits(['update', 'update:modelValue', 'focus', 'blur'])

const styles = computed(() =>
  defineStyles(
    {
      container: {
        color: {
          white: 'bg-white dark:bg-dark-800',
          transparent: ''
        },
        align: {
          center: 'text-center',
          left: 'text-left'
        },
        isDisabled: ['text-charcoal-500 !bg-charcoal-100 dark:!bg-dark-600', '']
      },
      input: {
        color: {
          white: 'bg-white dark:bg-dark-800',
          transparent: '!bg-transparent'
        },
        align: {
          center: 'text-center',
          left: 'text-left'
        },
        isDisabled: ['pointer-events-none text-charcoal-500 !bg-charcoal-100 dark:!bg-dark-600', ''],
        isHideHelpers: ['hide-helpers', ''],
        isUppercase: ['uppercase', '']
      }
    },
    props
  )
)

const isFocused = ref(false)

const isActive = computed(() => props.modelValue || props.modelValue === 0 || props.type == 'datetime-local' || props.type == 'date' || isFocused.value)

const getValue = (value: string | number) => {
  if (props.type == 'number') {
    return Number(value)
  }

  return value
}

const $input = ref()
const onClick = () => {
  if ($input.value) $input.value.focus()
}

const getHelper = (type: string) => {
  if (type == 'icon') return InputHelperIcon

  return 'div'
}

const onChange = () => {
  if (!$input.value) return

  emit('update:modelValue', getValue($input.value.value))
  emit('update', getValue($input.value.value))
}

const onBlur = () => {
  isFocused.value = false

  setTimeout(() => {
    emit('blur', props.id)
  }, 100)
}

defineExpose({
  focus: () => {
    if ($input.value) $input.value.focus()
  },
  blur: () => {
    if ($input.value) $input.value.blur()
  }
})
</script>

<style lang="scss" scoped>
  input[type='number'] {
    -moz-appearance:textfield;
  }

  input::placeholder {
    @apply text-light-600 dark:text-dark-400/60;
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }
</style>